import { Alert } from "antd";
import {
  CustomTable,
  DateToolTip,
  ReviewStatusTag,
  ServiceRequestTableFilters,
  TableHeaderBar,
  TableHeaderInfo,
} from "~/components";
import { NewServiceRequestButton } from "~/components";
import { ROUTES } from "~/utils";
import { useTranslation } from "react-i18next";
import { TFunction } from "i18next";
import {
  FormattedServiceRequestData,
  ServiceRequestEntityDetails,
} from "~/types";
import * as APITypes from "~/API";
import { ColumnsType } from "antd/es/table";
import { CustomTextField } from "~/components";
import { UserOutlined } from "@ant-design/icons";
import { convertCurrencyFromLowest, dateToLocalTime } from "~/utils";
import { useRouteActivity } from "~/hooks/useRouteActivity";

export const getServiceRequestTableColumns = (
  t: TFunction<"translation">
): ColumnsType<FormattedServiceRequestData> => [
  {
    title: t("Title"),
    dataIndex: "title",
    key: "title",
    width: 250,
    render: (_, record: FormattedServiceRequestData) => {
      const id = record.id;
      return (
        <CustomTextField
          value={record.title}
          link={`${ROUTES.serviceRequest}/${id}`}
        />
      );
    },
  },
  {
    title: t("Entity_Info"),
    dataIndex: "entity",
    key: "entity",
    width: 200,
    render: (_, record: FormattedServiceRequestData) => {
      const entityID = record.entityID;
      const entityTypeRaw = record.entityType;
      if (!entityTypeRaw) {
        return <CustomTextField value="" onEmpty="n/a" />;
      }
      const entityType =
        entityTypeRaw.toLowerCase() as ServiceRequestEntityDetails;
      const entityName = record.entityDetails?.[entityType]?.data?.name;
      return (
        <CustomTextField
          prefix={`${entityType}: `}
          value={entityName || ""}
          link={entityID ? `/${entityType}/${entityID}` : undefined}
          newPage={true}
        />
      );
    },
  },
  {
    title: t("Status"),
    dataIndex: "status",
    sorter: (a, b) => (a.status || "").localeCompare(b.status || ""),
    key: "status",
    width: 200,
    render: (_, record: FormattedServiceRequestData) => {
      const status = record.status;
      return <ReviewStatusTag status={status} />;
    },
  },
  {
    title: t("Handler"),
    dataIndex: "handlerName",
    key: "handlerName",
    width: 200,
    render: (_, record: FormattedServiceRequestData) => {
      const handlerName = record.handlerName;
      return <CustomTextField prefix={<UserOutlined />} value={handlerName} />;
    },
  },
  {
    title: (
      <>
        {t("Created_At")} <DateToolTip />
      </>
    ),
    dataIndex: "createdAt",
    sorter: (a, b) => {
      const dateA = a.createdAt;
      const dateB = b.createdAt;

      if (!dateA && !dateB) return 0;
      if (!dateA) return 1; // Empty dates go to the end
      if (!dateB) return -1; // Empty dates go to the end

      // Convert UTC date strings to Date objects and compare timestamps
      const timestampA = new Date(dateA).getTime();
      const timestampB = new Date(dateB).getTime();

      return timestampA - timestampB;
    },
    sortDirections: ["ascend", "descend"],
    defaultSortOrder: "descend",
    key: "createdAt",
    width: 200,
    render: (_, record: FormattedServiceRequestData) => {
      return (
        <CustomTextField
          value={dateToLocalTime(record.createdAt, "dateTime")}
          onEmpty="n/a"
        />
      );
    },
  },
  {
    title: (
      <>
        {t("Updated_At")} <DateToolTip />
      </>
    ),
    dataIndex: "updatedAt",
    sorter: (a, b) => {
      const dateA = a.updatedAt;
      const dateB = b.updatedAt;

      if (!dateA && !dateB) return 0;
      if (!dateA) return 1; // Empty dates go to the end
      if (!dateB) return -1; // Empty dates go to the end

      // Convert UTC date strings to Date objects and compare timestamps
      const timestampA = new Date(dateA).getTime();
      const timestampB = new Date(dateB).getTime();

      return timestampA - timestampB;
    },
    key: "updatedAt",
    width: 200,
    render: (_, record: FormattedServiceRequestData) => {
      return (
        <CustomTextField
          value={dateToLocalTime(record.updatedAt, "dateTime")}
          onEmpty="n/a"
        />
      );
    },
  },
  // {
  //   title: t("Estimated_Cost"),
  //   dataIndex: "estimatedCost",
  //   key: "estimatedCost",
  //   width: 200,
  //   render: (_, record: FormattedServiceRequestData) => {
  //     const estimatedCost = convertCurrencyFromLowest(record.estimatedCost);
  //     return <CustomTextField value={estimatedCost} />;
  //   },
  // },
  {
    title: t("Final_Cost"),
    dataIndex: "finalCost",
    key: "finalCost",
    width: 200,
    render: (_, record: FormattedServiceRequestData) => {
      const finalCost = convertCurrencyFromLowest(record.finalCost);
      return <CustomTextField value={finalCost} />;
    },
  },
  {
    title: t("Type"),
    dataIndex: "type",
    sorter: (a, b) => (a.type || "").localeCompare(b.type || ""),
    key: "type",
    width: 200,
    render: (_, record: FormattedServiceRequestData) => {
      return <CustomTextField value={t(record.type as any)} />;
    },
  },
  {
    title: t("Tenant"),
    dataIndex: "tenantName",
    key: "tenantName",
    width: 200,
    render: (_, record: FormattedServiceRequestData) => {
      const tenantName = record.tenantName;
      return <CustomTextField prefix={<UserOutlined />} value={tenantName} />;
    },
  },
  {
    title: t("Contractor"),
    dataIndex: "contractor",
    key: "contractor",
    width: 200,
    render: (_, record: FormattedServiceRequestData) => {
      const contractor = record.contractor;
      return <CustomTextField prefix={<UserOutlined />} value={contractor} />;
    },
  },
  // {
  //   title: t("Watcher"),
  //   dataIndex: "watcherName",
  //   key: "watcherName",
  //   width: 200,
  //   render: (_, record: FormattedServiceRequestData) => {
  //     const watcherName = record.watcherName;
  //     return <CustomTextField prefix={<UserOutlined />} value={watcherName} />;
  //   },
  // },
];

export const ServiceRequestTable = ({
  serviceRequestList,
  ownerID,
  ownerType,
  tenantID,
  tenantList,
  userName,
  entityType,
  entityID,
  canCreateRequest,
  subscriptionPlan,
  hasServiceRequestAccess,
}: {
  serviceRequestList: FormattedServiceRequestData[];
  ownerID: string;
  ownerType: APITypes.OwnerType;
  userName: string;
  tenantID?: string;
  tenantList: APITypes.Tenant[];
  entityType?: APITypes.EntityType;
  entityID?: string;
  canCreateRequest: boolean;
  subscriptionPlan?: APITypes.SubscriptionPlan;
  hasServiceRequestAccess?: boolean;
}) => {
  const { t } = useTranslation();
  const { isTableLoading } = useRouteActivity();

  // If subscription plan is provided and user doesn't have service request access, show banner
  if (
    subscriptionPlan &&
    hasServiceRequestAccess !== undefined &&
    !hasServiceRequestAccess
  ) {
    return (
      <Alert
        message={t("Insufficient_Permissions")}
        description={`Service requests are not available on your ${subscriptionPlan.name} subscription. Please upgrade to access service request management features.`}
        type="warning"
        showIcon
      />
    );
  }

  return (
    <>
      <section data-tour="sr-table-section" style={{ marginTop: "2rem" }}>
        <CustomTable
          tableHeaders={
            <>
              <TableHeaderBar
                left={
                  <TableHeaderInfo
                    title={t("All_Service_Requests_Info")}
                    count={serviceRequestList.length}
                    countLabel={t("Total_Records_Found")}
                  />
                }
                right={
                  <span data-tour="sr-create">
                    <NewServiceRequestButton
                      ownerID={ownerID}
                      ownerType={ownerType}
                      tenantList={tenantList}
                      canCreateRequest={canCreateRequest}
                      initialValues={{
                        creatorID: ownerID,
                        creatorName: userName,
                        handlerID: ownerID,
                        handlerName: userName,
                        watcherID: ownerID,
                        watcherName: userName,
                        entityID: entityID,
                        entityType: entityType,
                        tenantID: tenantID,
                      }}
                    />
                  </span>
                }
              />

              <ServiceRequestTableFilters />
            </>
          }
          tableProps={{
            dataSource: serviceRequestList || [],
            columns: getServiceRequestTableColumns(t),
            loading: isTableLoading,
          }}
        />
      </section>
    </>
  );
};
